import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";

interface NavItem {
    label: any;
    icon?: string;
    path: string;
    show: boolean;
    className?: string;
    end?: boolean;
}

interface OverflowMenuProps {
    items: NavItem[];
    rightContent?: React.ReactNode;
}

export const OverflowTabbedMenu: React.FC<OverflowMenuProps> = ({ items, rightContent }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const [visibleItems, setVisibleItems] = useState<NavItem[]>([]);
    const [overflowItems, setOverflowItems] = useState<NavItem[]>([]);

    const filteredItems = items.filter((i) => i.show);

    function arraysAreEqual(arr1: NavItem[], arr2: NavItem[]): boolean {
        if (arr1.length !== arr2.length) return false;
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i].path !== arr2[i].path) return false;

            // Helper function to extract text content from React elements
            const getTextContent = (label: any): string => {
                if (typeof label === 'string') return label;
                if (typeof label === 'number') return label.toString();
                if (typeof label === 'object' && label !== null) {
                    // If it's a React element, try to extract text content
                    if (label.props && label.props.children) {
                        if (Array.isArray(label.props.children)) {
                            return label.props.children.map(getTextContent).join('');
                        } else {
                            return getTextContent(label.props.children);
                        }
                    }
                }
                return '';
            };

            const text1 = getTextContent(arr1[i].label);
            const text2 = getTextContent(arr2[i].label);

            if (text1 !== text2) return false;
        }
        return true;
    }

    useEffect(() => {
        if (!containerRef.current || !measureRef.current) return;

        let frameId: number | null = null;

        const update = () => {
            if (frameId) return;

            frameId = requestAnimationFrame(() => {
                frameId = null;

                if (!containerRef.current || !measureRef.current) return;

                const containerWidth = containerRef.current.offsetWidth;
                const childWidths = Array.from(measureRef.current.children).map(
                    (el) => (el as HTMLElement).offsetWidth
                );

                let total = 0;
                const visible: NavItem[] = [];
                const overflow: NavItem[] = [];

                for (let i = 0; i < filteredItems.length; i++) {
                    total += childWidths[i];
                    if (total + 75 < containerWidth) {
                        visible.push(filteredItems[i]);
                    } else {
                        overflow.push(filteredItems[i]);
                    }
                }

                setVisibleItems((prev) =>
                    arraysAreEqual(prev, visible) ? prev : visible
                );
                setOverflowItems((prev) =>
                    arraysAreEqual(prev, overflow) ? prev : overflow
                );
            });
        };

        update();

        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(containerRef.current);

        window.addEventListener("resize", update);

        return () => {
            if (frameId) cancelAnimationFrame(frameId);
            resizeObserver.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [filteredItems]);

    const renderNavLink = (item: NavItem, dropdown?: React.ReactNode) => (
        <div key={Math.random()}>
            <NavLink
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                    `${isActive ? "active" : ""} ${dropdown ? "dropdown-toggle" : ""}`
                }
                {...(dropdown ? { "data-bs-toggle": "dropdown", "aria-expanded": "false" } : {})}
            >
                {item.icon && <i className={`${item.icon} fa-fw`} aria-hidden="true" />}
                {item.label}
                {dropdown && <span className="badge bg-info ms-1">{overflowItems.length}</span>}
            </NavLink>
            {dropdown}
        </div>
    );

    const renderMoreDropdown = () => (
        <ul className="dropdown-menu" style={{ zIndex: 1000 }}>
            {overflowItems.map((item) => (
                <li key={item.path}>{renderNavLink(item)}</li>
            ))}
        </ul>
    );

    return (
        <>
            <div
                ref={measureRef}
                style={{
                    position: "absolute",
                    visibility: "hidden",
                    height: 0,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                }}
            >
                {filteredItems.map((item) => renderNavLink(item))}
            </div>

            <div
                ref={containerRef}
                className="menu-top nav nav-tabs"
                style={{
                    display: "flex",
                    flexWrap: "nowrap",
                    position: "relative",
                }}
            >
                {visibleItems.map((item) => renderNavLink(item))}
                {overflowItems.length > 0 &&
                    renderNavLink(
                        {
                            label: "More",
                            path: "#",
                            show: true,
                        },
                        renderMoreDropdown()
                    )}
                {rightContent && (
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', paddingBottom: '3px' }}>
                        {rightContent}
                    </div>
                )}
            </div>
        </>
    );
};
